import type { QuerySpec } from '@helios/powersync-client-core';
import type { PatientCoreFieldDef, PatientFormStep } from '@helios/domain';

/**
 * Row shape returned from patient_field_definitions PowerSync table (override rows).
 * Column names must match the PowerSync schema in web-admin.
 * All override columns are nullable — only non-null values represent an override.
 */
export interface PatientFieldDefinitionRow {
  id: string;
  key: string;
  field_type: string;
  category: string;
  default_label: string | null;
  default_enabled: number | null; // 0 | 1 | null
  required_override: number | null; // 0 | 1 | null
  order_override: number | null;
  width_override: string | null; // 'half' | 'full' | null
  validation_override: string | null; // JSON string or null
  updated_at: string | null;
}

/**
 * View model for a single patient form field as displayed in settings UI.
 */
export type EffectivePatientField = {
  field_key: string;
  label: string;
  step: PatientFormStep;
  visible: boolean;
  required: boolean;
  order: number;
  width: 'half' | 'full';
  field_type?: string;
  locked?: boolean;
  notes?: string;
  /** Raw JSON string from patient_field_definitions.validation_override */
  validation_override: string | null;
  /** Tracks which properties have been overridden from code defaults */
  overrides: {
    label?: boolean;
    visible?: boolean;
    required?: boolean;
    order?: boolean;
    width?: boolean;
  };
};

/**
 * QuerySpec for fetching global override rows from patient_field_definitions.
 */
export function qPatientFieldDefinitionOverrides(): QuerySpec<[]> {
  return {
    key: ['patient-form', 'overrides'] as const,
    sql: `SELECT * FROM patient_field_definitions ORDER BY key`,
    params: [],
    watch: ['patient_field_definitions'],
  };
}

/**
 * Pure merge: (code defaults) + (DB overrides) → effective settings VM.
 *
 * - Always returns one row per core field (never empty if coreFields is populated).
 * - DB rows only override non-null properties; code defaults fill the rest.
 * - The `overrides` map tracks which properties came from DB for UI hints.
 */
export function buildPatientFormSettingsVM(
  coreFields: readonly PatientCoreFieldDef[],
  dbOverrides: PatientFieldDefinitionRow[],
): EffectivePatientField[] {
  // Index DB rows by field_key for O(1) lookup
  const overrideMap = new Map<string, PatientFieldDefinitionRow>();
  for (const row of dbOverrides) {
    overrideMap.set(row.key, row);
  }

  return coreFields.map((def) => {
    const dbRow = overrideMap.get(def.field_key);
    const overrides: EffectivePatientField['overrides'] = {};

    let label = def.label;
    let visible = def.visible;
    let required = def.required;
    let order = def.order;
    let width: 'half' | 'full' = 'half';
    let validation_override: string | null = null;

    if (dbRow) {
      // Apply DB overrides where present (null = no override)
      if (dbRow.default_label != null) {
        label = dbRow.default_label;
        overrides.label = true;
      }
      if (dbRow.default_enabled != null) {
        visible = dbRow.default_enabled === 1;
        overrides.visible = true;
      }
      if (dbRow.required_override != null) {
        required = dbRow.required_override === 1;
        overrides.required = true;
      }
      if (dbRow.order_override != null) {
        order = dbRow.order_override;
        overrides.order = true;
      }
      if (dbRow.width_override === 'half' || dbRow.width_override === 'full') {
        width = dbRow.width_override;
        overrides.width = true;
      }
      if (dbRow.validation_override != null) {
        validation_override = dbRow.validation_override;
      }
    }

    return {
      field_key: def.field_key,
      label,
      step: def.step,
      visible,
      required,
      order,
      width,
      field_type: def.type,
      locked: def.locked,
      notes: def.notes,
      validation_override,
      overrides,
    };
  });
}

// ─── ID Type options (idType field config) ───

/**
 * Stored under `validation_override.idTypeOptions` for the `idType` field.
 * Lets a clinic decide which ID types appear in the Patient Form's "ID Type"
 * dropdown — both predefined entries can be toggled and custom entries can
 * be added.
 */
export interface IdTypeOptionsConfig {
  /** Ordered list of ID type labels presented to the user. */
  types: string[];
}

/** Defaults applied when a clinic hasn't customised the list yet. */
export const DEFAULT_ID_TYPES: string[] = [
  'National ID',
  'Passport',
  'Driver License',
  'SSN',
  'Other',
];

/** Predefined catalog the settings page surfaces as toggle-able presets. */
export const PREDEFINED_ID_TYPES: string[] = [
  'National ID',
  'Passport',
  'Driver License',
  'SSN',
  'Aadhaar',
  'PAN',
  'Voter ID',
  'Health Card',
  'Insurance Card',
  'Other',
];

/** Parse the idTypeOptions config from a field's `validation_override` JSON. */
export function parseIdTypeOptions(
  validationOverride: string | null | undefined,
): IdTypeOptionsConfig {
  if (!validationOverride) return { types: DEFAULT_ID_TYPES };
  try {
    const parsed = JSON.parse(validationOverride) as { idTypeOptions?: unknown };
    const cfg = parsed?.idTypeOptions as { types?: unknown } | undefined;
    if (cfg && Array.isArray(cfg.types)) {
      const cleaned = cfg.types
        .filter((t): t is string => typeof t === 'string')
        .map((t) => t.trim())
        .filter((t) => t.length > 0);
      if (cleaned.length > 0) return { types: cleaned };
    }
  } catch {
    /* ignore */
  }
  return { types: DEFAULT_ID_TYPES };
}

/**
 * Merge a new `idTypeOptions` config into an existing `validation_override`
 * JSON string, preserving any other keys (e.g. validation rules). Returns
 * the new JSON string, or null when the result is empty.
 */
export function writeIdTypeOptions(
  validationOverride: string | null,
  config: IdTypeOptionsConfig | null,
): string | null {
  let existing: Record<string, unknown> = {};
  if (validationOverride) {
    try {
      existing = JSON.parse(validationOverride) as Record<string, unknown>;
    } catch {
      /* fall through to empty */
    }
  }
  if (config) {
    existing.idTypeOptions = { types: config.types };
  } else {
    delete existing.idTypeOptions;
  }
  if (Object.keys(existing).length === 0) return null;
  return JSON.stringify(existing);
}

/**
 * Builds a merged ValidationSchema from per-field validation_override strings.
 * Returns a JSON string suitable for IntakeRenderer's validationSchema prop,
 * or null if no fields have validation overrides.
 */
export function buildPatientValidationSchema(fields: EffectivePatientField[]): string | null {
  const fieldEntries: Record<string, unknown> = {};
  for (const f of fields) {
    if (!f.validation_override) continue;
    try {
      const parsed = JSON.parse(f.validation_override);
      if (parsed && Array.isArray(parsed.rules) && parsed.rules.length > 0) {
        fieldEntries[f.field_key] = parsed;
      }
    } catch {
      /* skip invalid JSON */
    }
  }
  if (Object.keys(fieldEntries).length === 0) return null;
  return JSON.stringify({ version: 1, fields: fieldEntries });
}
